import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiChevronRight, FiExternalLink,
} from "react-icons/fi";
import {
  MdVpnKey, MdSettings, MdSecurity, MdChecklist, MdBugReport,
  MdLink, MdNotifications, MdCode, MdInfoOutline, MdWarningAmber,
} from "react-icons/md";
import { HiOutlineServer } from "react-icons/hi";
import { TbApi, TbListCheck } from "react-icons/tb";
import servicenow1Img from "../../assets/servicenow1.png";
import servicenow2Img from "../../assets/servicenow2.png";
import servicenow4Img from "../../assets/servicenow4.png";
import servicenow5Img from "../../assets/servicenow5.png";
import servicenow6Img from "../../assets/servicenow6.png";
import servicenow9Img from "../../assets/servicenow9.png";
import servicenow10Img from "../../assets/servicenow10.png";
import servicenow11Img from "../../assets/servicenow11.png";
import servicenow12Img from "../../assets/servicenow12.png";

/* ─── tiny shared primitives (kept consistent with Services.jsx) ─── */
const Breadcrumb = ({ items }) => (
  <nav className="flex flex-wrap items-center gap-2 text-sm text-[#9a734c] mb-8">
    {items.map((item, i) => (
      <span key={i} className="flex items-center gap-2">
        {i > 0 && <FiChevronRight className="text-[14px]" />}
        {item.href
          ? <a className="hover:text-primary hover:underline" href={item.href}>{item.label}</a>
          : <span className="text-slate-900 dark:text-white">{item.label}</span>}
      </span>
    ))}
  </nav>
);

const SectionHeading = ({ icon, label, id, color = "primary/10 text-primary" }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className={`w-8 h-8 rounded bg-${color} flex items-center justify-center`}>
      {icon}
    </div>
    <h2 id={id} className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white scroll-mt-24">
      {label}
    </h2>
  </div>
);

const SubHeading = ({ label, id }) => (
  <h3 id={id} className="text-lg font-bold text-slate-900 dark:text-white scroll-mt-24">
    {label}
  </h3>
);

const StepItem = ({ num, children }) => (
  <li className="flex gap-3 items-start">
    <span className="mt-0.5 min-w-[1.5rem] h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">{num}</span>
    <span className="text-slate-600 dark:text-slate-400 leading-relaxed">{children}</span>
  </li>
);

const BulletItem = ({ children }) => (
  <li className="flex gap-3 items-start">
    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary shrink-0"></span>
    <span className="text-slate-600 dark:text-slate-400 leading-relaxed">{children}</span>
  </li>
);

const InfoBox = ({ tone = "amber", label, icon, children }) => {
  const tones = {
    amber: "bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300",
    red: "bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300",
    slate: "bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400",
    orange: "bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800 text-orange-700 dark:text-orange-300",
  };
  return (
    <div className={`rounded-xl p-5 border ${tones[tone]}`}>
      {label && (
        <p className="text-sm font-semibold mb-1.5 flex items-center gap-2">
          {icon}{label}
        </p>
      )}
      <div className="text-sm leading-relaxed">{children}</div>
    </div>
  );
};

const StepImage = ({ src, alt }) => (
  <img
    src={src}
    alt={alt}
    className="mt-2 rounded-lg border border-slate-200 dark:border-white/10 shadow-md"
  />
);

const DataTable = ({ headers, rows }) => (
  <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
    <table className="w-full text-sm">
      <thead className="bg-slate-50 dark:bg-slate-800/50">
        <tr>
          {headers.map(h => (
            <th key={h} className="text-left font-semibold text-slate-700 dark:text-slate-300 px-4 py-3 border-b border-slate-200 dark:border-slate-800 whitespace-nowrap">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
        {rows.map((row, i) => (
          <tr key={i} className="bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
            {row.map((cell, j) => (
              <td key={j} className="px-4 py-3 text-slate-600 dark:text-slate-400 align-top leading-relaxed">
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const DiagramBlock = ({ children }) => (
  <pre className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-900 dark:bg-black text-slate-100 text-xs sm:text-sm p-5 overflow-x-auto leading-relaxed font-mono whitespace-pre">
    {children}
  </pre>
);

const ChecklistGrid = ({ items }) => (
  <div className="grid sm:grid-cols-2 gap-2">
    {items.map((item, i) => (
      <div key={i} className="flex items-start gap-2.5 px-4 py-2.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
        <span className="mt-0.5 w-4 h-4 rounded border-2 border-primary/50 shrink-0"></span>
        <span className="text-sm text-slate-700 dark:text-slate-300 leading-snug">{item}</span>
      </div>
    ))}
  </div>
);

const ReferenceLink = ({ label, href }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    className="flex items-center justify-between gap-4 px-5 py-3.5 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-primary/50 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group"
  >
    <span className="text-sm text-slate-700 dark:text-slate-300 group-hover:text-primary">{label}</span>
    <FiExternalLink className="text-slate-400 group-hover:text-primary shrink-0" />
  </a>
);

export function renderServicenow() {
  return <ServiceNowComponent />;
}

function ServiceNowComponent() {
  const navigate = useNavigate();

  return (
    <main className="transition-opacity duration-300 space-y-16">

      {/* Breadcrumbs */}
      <Breadcrumb items={[
        { label: "Netraa APM" },
        { label: "Integrations" },
        { label: "ServiceNow" }
      ]} />

      {/* PAGE HEADING */}
      <div className="border-b border-gray-200 dark:border-gray-800 pb-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 scroll-mt-24" id="intro">
          <h1 className="text-3xl lg:text-5xl font-extrabold text-text-main dark:text-white tracking-tight leading-tight max-w-[800px]">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-600">
              ServiceNow Integration:
            </span>{" "}
            Configuration Guide
          </h1>
          <div className="flex-shrink-0">
            {/* <button
              onClick={() => {
                navigate("/adminconfiguration", { state: { scrollTo: "InterfaceConfiguration" } });
              }}
              className="px-5 py-3 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 text-orange-600 dark:text-orange-400 rounded-xl font-semibold whitespace-nowrap hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors inline-flex items-center gap-2"
            >
              <FiChevronRight className="rotate-180 w-4 h-4" />
              Open Interface Configuration
            </button> */}
          </div>
        </div>

        <p className="mt-4 text-lg lg:text-xl text-slate-500 dark:text-slate-400 leading-relaxed font-light max-w-[900px]">
          End-to-end configuration for OAuth, Interface Configuration, and Alert-to-Ticket integration between Netraa APM and ServiceNow.
        </p>

        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-2 gap-4">
          <div className="group rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 shadow-sm hover:shadow-md hover:border-orange-300 dark:hover:border-orange-700 transition-all duration-200">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 rounded-lg bg-orange-50 dark:bg-orange-950 text-orange-600 dark:text-orange-400">
                <HiOutlineServer className="w-4 h-4" />
              </div>
              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Application</p>
            </div>
            <p className="text-sm font-semibold text-slate-800 dark:text-white">Netraa APM</p>
          </div>

          <div className="group rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 shadow-sm hover:shadow-md hover:border-red-300 dark:hover:border-red-700 transition-all duration-200">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 rounded-lg bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400">
                <TbApi className="w-4 h-4" />
              </div>
              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Integration</p>
            </div>
            <p className="text-sm font-semibold text-slate-800 dark:text-white">ServiceNow</p>
          </div>

          <div className="group rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 shadow-sm hover:shadow-md hover:border-amber-300 dark:hover:border-amber-700 transition-all duration-200">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 rounded-lg bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400">
                <MdVpnKey className="w-4 h-4" />
              </div>
              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Authentication</p>
            </div>
            <p className="text-sm font-semibold text-slate-800 dark:text-white">OAuth 2.0 – Client Credentials</p>
          </div>

          <div className="group rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 shadow-sm hover:shadow-md hover:border-slate-400 dark:hover:border-slate-600 transition-all duration-200">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                <MdInfoOutline className="w-4 h-4" />
              </div>
              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Document Purpose</p>
            </div>
            <p className="text-sm font-semibold text-slate-800 dark:text-white leading-snug">Enable a new administrator to configure ServiceNow ticket creation from Netraa alerts</p>
          </div>
        </div>
      </div>

      {/* Purpose */}
      <section id="purpose" className="scroll-mt-24 space-y-4">
        <SectionHeading icon={<MdInfoOutline className="text-xl" />} label="Purpose" id="purpose" />
        <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
          This document explains how to configure the ServiceNow ticketing tool in Netraa APM. It covers from creating the OAuth in application registry to raising a ticket in ServiceNow through Netraa.
        </p>
      </section>

      {/* Integration Overview */}
      <section id="integration-overview" className="scroll-mt-24 space-y-4">
        <SectionHeading icon={<TbApi className="text-xl" />} label="Integration Overview" id="integration-overview" />
        <p className="text-slate-600 dark:text-slate-400 leading-relaxed">The complete flow is:</p>
        <DiagramBlock>{`ServiceNow Instance
      |
      | 1. Create OAuth in application registry
      | 2. Obtain Client ID + Client Secret
      | 3. Configure OAuth access / scope 
      v
Netraa APM -> Admin Configuration -> Interface Configuration
      |
      | Select Service Now + instance + credentials + record type
      v
Create Interface
      |
      v
Alerts -> Set Alert -> Alert Action = Service Now
      |
      v
Threshold Breach
      |
      v
ServiceNow Record / Ticket Created`}</DiagramBlock>
      </section>

      {/* Prerequisites */}
      <section id="prerequisites" className="scroll-mt-24 space-y-4">
        <SectionHeading icon={<HiOutlineServer className="text-xl" />} label="Prerequisites" id="prerequisites" />
        <ul className="space-y-3">
          <BulletItem>A working ServiceNow instance URL, for example <code className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm">https://&lt;instance&gt;.service-now.com</code>.</BulletItem>
          <BulletItem>Administrative access to the ServiceNow instance. ServiceNow documentation specifies the <strong className="text-slate-800 dark:text-slate-200">oauth_admin</strong> role for OAuth application endpoint configuration.</BulletItem>
          <BulletItem>Access to Netraa APM Admin Configuration.</BulletItem>
          <BulletItem>A ServiceNow integration user/caller appropriate for the integration.</BulletItem>
          <BulletItem>The ServiceNow record type that Netraa should create: Incident, Change, Catalogs, or Problems.</BulletItem>
          <BulletItem>The required ServiceNow OAuth scope and permissions for the integration.</BulletItem>
        </ul>
        <InfoBox tone="amber" label="Important" icon={<MdWarningAmber className="text-base" />}>
          Do not share the Client Secret in screenshots, email, source code, browser-side code, or documentation intended for general users. Store it securely.
        </InfoBox>
      </section>

      {/* Step 1 – Create OAuth Application in ServiceNow */}
      <section id="step1-oauth-app" className="scroll-mt-24 space-y-6">
        <SectionHeading icon={<MdVpnKey className="text-xl" />} label="Create OAuth Application in ServiceNow" id="step1-oauth-app" />
        <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
          Netraa uses OAuth 2.0 to authenticate to the ServiceNow REST APIs. For the Client Credentials flow, the client sends the Client ID and Client Secret to ServiceNow's OAuth token endpoint and receives an access token. ServiceNow's current documentation describes this as the client credentials grant workflow.
        </p>

        <div className="space-y-3">
          <SubHeading label="Open Application Registry" id="open-app-registry" />
          <ol className="space-y-3">
            <StepItem num={1}>Log in to the ServiceNow instance that Netraa will connect to.</StepItem>
            <StepItem num={2}>Open the Application Navigator / All menu.</StepItem>
            <StepItem num={3}>Navigate to <strong className="text-slate-800 dark:text-slate-200">System OAuth -&gt; Application Registry</strong>.</StepItem>
            <StepImage src={servicenow4Img} alt="Interface Configuration – Connection Details" />
            <StepItem num={4}>Click <strong className="text-slate-800 dark:text-slate-200">New</strong>.</StepItem>
            <StepImage src={servicenow5Img} alt="Interface Configuration – Connection Details" />
            <StepItem num={5}>Select <strong className="text-slate-800 dark:text-slate-200">New inbound integration Experience</strong>.</StepItem>
            <StepImage src={servicenow6Img} alt="Interface Configuration – Connection Details" />
            <StepItem num={6}>Select <strong className="text-slate-800 dark:text-slate-200">New Integration</strong>.</StepItem>
            <StepImage src={servicenow9Img} alt="Interface Configuration – Connection Details" />
            <StepItem num={7}>Select <strong className="text-slate-800 dark:text-slate-200">Oauth Client Credentials grant</strong>.</StepItem>
            <StepImage src={servicenow10Img} alt="Interface Configuration – Connection Details" />
            
          </ol>
          <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
            ServiceNow documents this path as System OAuth &gt; Application Registry &gt; New &gt; New inbound integration Experience.
          </p>
        </div>

        <div className="space-y-3">
          <SubHeading label="Fill the OAuth Application" id="fill-oauth-app" />
          <DataTable
            headers={["Field", "What to enter", "Purpose"]}
            rows={[
              ["Name", "A unique name such as Netraa APM Integration", "Identifies the OAuth application."],
              ["Client ID", "Generated by ServiceNow", "Unique identifier used by Netraa when requesting an access token."],
              ["Client Secret", "Generate or use the secret provided by ServiceNow", "Secret used with the Client ID to authenticate Netraa."],
            ]}
          />
        </div>
            <StepImage src={servicenow11Img} alt="Interface Configuration – Connection Details" />

        <div className="space-y-3">
          <SubHeading label="Record the Client ID and Client Secret" id="record-client-id" />
          <ol className="space-y-3">
            <StepItem num={6}>Copy the Client ID generated for the OAuth application.</StepItem>
            <StepItem num={7}>Copy the Client Secret and store it securely.</StepItem>
            <StepItem num={8}>Do not confuse the Client Secret with the ServiceNow user password.</StepItem>
            <StepItem num={9}>The values will later be entered into Netraa Admin Configuration -&gt; Interface Configuration.</StepItem>
          </ol>
          <InfoBox tone="red" label="Security" icon={<MdSecurity className="text-base" />}>
            The Client Secret is a credential. Anyone who obtains it may be able to authenticate as the integration application, subject to the configured scope and permissions.
          </InfoBox>
        </div>
      </section>

      {/* Step 2 – Configure Client Credentials Access */}
      <section id="step2-client-credentials" className="scroll-mt-24 space-y-4">
        <SectionHeading icon={<MdSettings className="text-xl" />} label="Configure Client Credentials Access" id="step2-client-credentials" />
        <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
          Netraa's ServiceNow configuration uses the Client Credentials grant type. ServiceNow's current documentation states that inbound Client Credentials support requires the relevant OAuth capability to be enabled and an OAuth Application User to be associated with the OAuth client. The application user controls the user context under which the authorization is passed.
        </p>
        <ol className="space-y-3">
          <StepItem num={10}>Open the OAuth application record created in Application Registry.</StepItem>
          <StepItem num={11}>Ensure the Client Credentials grant is enabled/configured for the instance according to your ServiceNow version and security policy.</StepItem>
          <StepItem num={12}>If required by the instance configuration, add the OAuth Application User field to the OAuth Entity form.</StepItem>
          <StepItem num={13}>Set the OAuth Application User to the ServiceNow integration user that Netraa should operate as.</StepItem>
          <StepItem num={14}>Save/Update the OAuth application.</StepItem>
        </ol>
        <InfoBox tone="slate" label="ServiceNow requirement">
          For Client Credentials integrations, ServiceNow documents the OAuth Application User and the REST API Auth Scope as controls for the access granted to the third-party client.
        </InfoBox>
      </section>

      {/* Step 3 – Configure the OAuth Scope */}
      <section id="step3-oauth-scope" className="scroll-mt-24 space-y-4">
        <SectionHeading icon={<MdVpnKey className="text-xl" />} label="Configure the OAuth Scope" id="step3-oauth-scope" />
        <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
          The OAuth scope controls which protected resources the issued access token can access. Netraa's Interface Configuration contains a Scope field, so the value configured in Netraa must correspond to the scope configured and permitted in ServiceNow.
        </p>
        <ol className="space-y-3">
          <StepItem num={15}>Open or create the appropriate ServiceNow OAuth scope/profile configuration.</StepItem>
          <StepItem num={16}>Use the scope name approved for the Netraa integration.</StepItem>
          <StepItem num={17}>Make sure the scope provides the permissions required for the ServiceNow record type being used.</StepItem>
          <StepItem num={18}>Save the scope configuration.</StepItem>
          <StepItem num={19}>Use the exact scope string in Netraa. Do not add extra spaces.</StepItem>
        </ol>
        <div>
          <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Example scope:</p>
          <DiagramBlock>{`netraa_incident_scope`}</DiagramBlock>

        </div>
      </section>

      {/* Step 4 – Verify the OAuth Token Endpoint */}
      <section id="step4-token-endpoint" className="scroll-mt-24 space-y-4">
        <SectionHeading icon={<MdLink className="text-xl" />} label="Verify the OAuth Token Endpoint" id="step4-token-endpoint" />
        <p className="text-slate-600 dark:text-slate-400 leading-relaxed">For a ServiceNow instance, the OAuth token endpoint is:</p>
        <DiagramBlock>{`https://<your-instance>.service-now.com/oauth_token.do`}</DiagramBlock>
        <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
          For example, if the ServiceNow instance is <code className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm">dev392581.service-now.com</code>:
        </p>
        <DiagramBlock>{`https://dev392581.service-now.com/oauth_token.do`}</DiagramBlock>
        <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
          ServiceNow documents /oauth_token.do as the token endpoint for OAuth token requests.
        </p>
      </section>

      {/* Optional OAuth Connectivity Test */}
      <section id="oauth-connectivity-test" className="scroll-mt-24 space-y-4">
        <SectionHeading icon={<MdCode className="text-xl" />} label="Optional OAuth Connectivity Test" id="oauth-connectivity-test" />
        <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
          Before configuring Netraa, you can verify the OAuth credentials independently using Postman or cURL.
        </p>
        <DiagramBlock>{`POST https://<instance>.service-now.com/oauth_token.do
Content-Type: application/x-www-form-urlencoded

grant_type=client_credentials
client_id=<CLIENT_ID>
client_secret=<CLIENT_SECRET>
scope=<SCOPE>`}</DiagramBlock>
        <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
          A successful response should contain an access token. The exact response fields and token lifetime depend on the ServiceNow configuration.
        </p>
      </section>

      {/* Step 5 – Configure Netraa Interface */}
      <section id="step5-netraa-interface" className="scroll-mt-24 space-y-6">
        <SectionHeading icon={<TbApi className="text-xl" />} label="Configure Netraa Interface" id="step5-netraa-interface" />
        <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
          After ServiceNow OAuth is configured, configure the corresponding interface in Netraa.
        </p>
        <ol className="space-y-3">
          <StepItem num={20}>Log in to Netraa APM.</StepItem>
          <StepItem num={21}>Open Admin Configuration.</StepItem>
          <StepItem num={22}>Under Network &amp; System, select Interface Configuration.</StepItem>
          <StepItem num={23}>Open the Create Interface section.</StepItem>
        </ol>

        <div className="space-y-3">
          <SubHeading label="Interface Configuration – Connection Details" id="connection-details" />
          <DataTable
            headers={["Netraa Field", "Value", "Example / Notes"]}
            rows={[
              ["Configuration Type", "Service Now", "Select Service Now from the dropdown."],
              ["Protocol", "https", "Use HTTPS for the ServiceNow instance."],
              ["IP / Domain", "ServiceNow instance hostname", "Example: dev392581.service-now.com"],
              ["Port", "443", "Standard HTTPS port."],
              ["Client Name", "Configured Netraa client", "Select the appropriate client from the dropdown."],
            ]}
          />
            <StepImage src={servicenow1Img} alt="Interface Configuration – Connection Details" />

        </div>

        <div className="space-y-3">
          <SubHeading label="Authentication & Access" id="auth-access" />
          <DataTable
            headers={["Field", "Value", "Notes"]}
            rows={[
              ["Authentication Type", "OAuth 2.0", "Select OAuth 2.0."],
              ["Grant Type", "Client Credentials", "Must match the OAuth setup."],
              ["Client ID", "ServiceNow OAuth Client ID", "Copy from the ServiceNow Application Registry."],
              ["Client Secret", "ServiceNow OAuth Client Secret", "Copy from the ServiceNow Application Registry."],
              ["Token URL", "https://<instance>.service-now.com/oauth_token.do", "Token endpoint of the target ServiceNow instance."],
              ["Scope", "Configured ServiceNow scope", "Example: netraa_incident_scope."],
              ["ServiceNow Record Type", "Incident / Change / Catalogs / Problems", "Select the record type Netraa should use."],
            ]}
          />
          <StepImage src={servicenow2Img} alt="Interface Configuration – Connection Details" />

        </div>
      </section>

      {/* Step 6 – Configure ServiceNow Caller */}
      <section id="step6-caller" className="scroll-mt-24 space-y-4">
        <SectionHeading icon={<MdSettings className="text-xl" />} label="Configure ServiceNow Caller" id="step6-caller" />
        <p className="text-sm text-slate-500 dark:text-slate-400 -mt-2">Applicable when ServiceNow Record Type = Incident.</p>
        <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
          The Caller identifies the ServiceNow user associated with the generated record. The caller must be an appropriate ServiceNow user and must have the permissions required by the integration and selected record type.
        </p>
        <ol className="space-y-3">
          <StepItem num={24}>Make sure the required ServiceNow integration/caller user exists.</StepItem>
          <StepItem num={25}>Verify that the user is active.</StepItem>
          <StepItem num={26}>Verify that the user has the roles/permissions required to create and access the selected record type.</StepItem>
          <StepItem num={27}>In Netraa's ServiceNow interface configuration, select the ServiceNow caller where the Caller field is available.</StepItem>
          <StepItem num={28}>Save the interface configuration.</StepItem>
        </ol>
        <InfoBox tone="orange" label="Recommendation">
          Do not use a personal administrator account unless your organization's security policy explicitly permits it. A dedicated integration user is preferable.
        </InfoBox>
      </section>

      {/* Step 7 – Select ServiceNow Record Type */}
      <section id="step7-record-type" className="scroll-mt-24 space-y-4">
        <SectionHeading icon={<TbListCheck className="text-xl" />} label="Select ServiceNow Record Type" id="step7-record-type" />
        <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
          Netraa supports the following ServiceNow record types in the interface configuration:
        </p>
        <ul className="space-y-3">
          <BulletItem><strong className="text-slate-800 dark:text-slate-200">Incident</strong> – operational incident/ticket raised when a monitored service or component has an issue.</BulletItem>
          <BulletItem><strong className="text-slate-800 dark:text-slate-200">Change</strong> – change-management record for controlled infrastructure/application changes.</BulletItem>
          <BulletItem><strong className="text-slate-800 dark:text-slate-200">Catalogs</strong> – service catalog/request-oriented records, depending on the Netraa-to-ServiceNow mapping.</BulletItem>
          <BulletItem><strong className="text-slate-800 dark:text-slate-200">Problems</strong> – problem-management records used to track underlying/root-cause issues.</BulletItem>
        </ul>
        <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
          Select the record type according to the ServiceNow workflow your organization wants Netraa to invoke.
        </p>
      </section>

      {/* Step 8 – Create the Interface */}
      <section id="step8-create-interface" className="scroll-mt-24 space-y-4">
        <SectionHeading icon={<MdSettings className="text-xl" />} label="Create the Interface" id="step8-create-interface" />
        <ol className="space-y-3">
          <StepItem num={29}>Review all connection fields.</StepItem>
          <StepItem num={30}>Review OAuth fields.</StepItem>
          <StepItem num={31}>Confirm the Client ID and Client Secret.</StepItem>
          <StepItem num={32}>Confirm the Token URL.</StepItem>
          <StepItem num={33}>Confirm the Scope.</StepItem>
          <StepItem num={34}>Confirm the ServiceNow Record Type.</StepItem>
          <StepItem num={35}>Confirm the ServiceNow Caller.</StepItem>
          <StepItem num={36}>Click Test Connection before creating the interface.</StepItem>
        </ol>
        <InfoBox tone="amber" label="Test Connection" icon={<MdWarningAmber className="text-base" />}>
          This step must be completed successfully before the interface is created. This validates the ServiceNow connectivity and the configured authentication details using the values entered above.
        </InfoBox>
        <InfoBox tone="red" label="If the test fails" icon={<MdWarningAmber className="text-base" />}>
          Do not click Create Interface. Correct the domain, port, OAuth credentials, token URL, scope, caller/permissions, or network configuration and run Test Connection again.
        </InfoBox>
        <ol className="space-y-3" start={37}>
          <StepItem num={37}>After Test Connection returns a successful result, click Create Interface.</StepItem>
          <StepItem num={38}>Verify that the interface is listed as a configured ServiceNow interface.</StepItem>
        </ol>
      </section>

      {/* Recommended Configuration Example */}
      <section id="config-example" className="scroll-mt-24 space-y-4">
        <SectionHeading icon={<MdChecklist className="text-xl" />} label="Recommended Configuration Example" id="config-example" />
        <DataTable
          headers={["Field", "Example"]}
          rows={[
            ["Configuration Type", "Service Now"],
            ["Protocol", "https"],
            ["IP / Domain", "dev392581.service-now.com"],
            ["Port", "443"],
            ["Authentication Type", "OAuth 2.0"],
            ["Grant Type", "Client Credentials"],
            ["Client ID", "<copied from ServiceNow>"],
            ["Client Secret", "<copied from ServiceNow>"],
            ["Token URL", "https://dev392581.service-now.com/oauth_token.do"],
            ["Scope", "netraa_incident_scope"],
            ["ServiceNow Record Type", "Incident"],
            ["Caller", "Configured ServiceNow integration user"],
          ]}
        />

      </section>

      {/* Step 9 – Configure an Alert in Netraa */}
      <section id="step9-alert" className="scroll-mt-24 space-y-4">
        <SectionHeading icon={<MdNotifications className="text-xl" />} label="Configure an Alert in Netraa" id="step9-alert" />
        <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
          After the ServiceNow interface has been created, associate it with an alert configuration. The alert determines when Netraa should create the ServiceNow record.
        </p>
        <ol className="space-y-3">
          <StepItem num={39}>Open the Alerts section in Netraa.</StepItem>
          <StepItem num={40}>Open the Set Alert tab.</StepItem>
          <StepItem num={41}>Create a new alert or edit the required alert configuration.</StepItem>
          <StepItem num={42}>Configure the application/service/metric and threshold according to the monitoring requirement.</StepItem>
          <StepItem num={43}>Open the Alert Action section.</StepItem>
          <StepItem num={44}>Select Service Now as the alert action.</StepItem>
          <StepItem num={45}>Select the ServiceNow instance/interface that was created in Admin Configuration -&gt; Interface Configuration.</StepItem>
          <StepItem num={46}>Configure the warning/critical threshold and other alert conditions.</StepItem>
          <StepItem num={47}>Save/Create the alert.</StepItem>
        </ol>
        <InfoBox tone="slate" label="Note">
          When the ServiceNow Record Type is Problem, also call out the ServiceNow group while configuring the alert conditions.
        </InfoBox>
        <StepImage src={servicenow12Img} alt="Interface Configuration – Connection Details" />

      </section>

      {/* End-to-End Ticket Creation Flow */}
      <section id="end-to-end-flow" className="scroll-mt-24 space-y-4">
        <SectionHeading icon={<TbApi className="text-xl" />} label="End-to-End Ticket Creation Flow" id="end-to-end-flow" />
        <DiagramBlock>{`Metric Data
   |
   v
Netraa Alert Evaluation
   |
   | Threshold breached?
   |
   +---- No ----> No ServiceNow ticket
   |
  Yes
   |
   v
Alert Action = ServiceNow
   |
   v
Selected ServiceNow Interface
   |
   | OAuth Client Credentials
   v
POST /oauth_token.do
   |
   v
Access Token
   |
   v
ServiceNow REST API
   |
   v
Incident / Change / Catalog / Problem`}</DiagramBlock>
      </section>

      {/* How to Verify That the Integration Works */}
      <section id="verify-integration" className="scroll-mt-24 space-y-6">
        <SectionHeading icon={<TbListCheck className="text-xl" />} label="How to Verify That the Integration Works" id="verify-integration" />

        <div className="space-y-3">
          <SubHeading label="Verify in Netraa" id="verify-netraa" />
          <ul className="space-y-3">
            <BulletItem>The ServiceNow interface exists under Admin Configuration -&gt; Interface Configuration.</BulletItem>
            <BulletItem>The interface contains the correct instance domain.</BulletItem>
            <BulletItem>OAuth 2.0 and Client Credentials are selected.</BulletItem>
            <BulletItem>The Client ID, Client Secret, Token URL, and Scope are correct.</BulletItem>
            <BulletItem>A ServiceNow record type is selected.</BulletItem>
            <BulletItem>The ServiceNow caller is configured.</BulletItem>
            <BulletItem>The alert action is set to Service Now.</BulletItem>
            <BulletItem>The alert references the correct ServiceNow interface.</BulletItem>
          </ul>
        </div>

        <div className="space-y-3">
          <SubHeading label="Verify in ServiceNow" id="verify-servicenow" />
          <ul className="space-y-3">
            <BulletItem>The OAuth application exists in System OAuth -&gt; Application Registry.</BulletItem>
            <BulletItem>The Client ID belongs to the expected Netraa integration.</BulletItem>
            <BulletItem>The Client Secret is valid.</BulletItem>
            <BulletItem>Client Credentials access is enabled/configured as required.</BulletItem>
            <BulletItem>The OAuth Application User is configured when required.</BulletItem>
            <BulletItem>The required scope is available and grants the intended access.</BulletItem>
            <BulletItem>The caller/integration user is active and authorized.</BulletItem>
            <BulletItem>The expected record appears after the Netraa threshold breach.</BulletItem>
          </ul>
        </div>
      </section>

      {/* Common Problems and Troubleshooting */}
      <section id="troubleshooting" className="scroll-mt-24 space-y-4">
        <SectionHeading icon={<MdBugReport className="text-xl" />} label="Common Problems and Troubleshooting" id="troubleshooting" />
        <DataTable
          headers={["Problem", "Likely Cause", "Check"]}
          rows={[
            ["401 / Unauthorized", "Invalid Client ID/Secret or OAuth configuration", "Verify credentials and Client Credentials configuration."],
            ["403 / Forbidden", "Scope or ServiceNow user permissions are insufficient", "Verify OAuth scope and integration user's roles/permissions."],
            ["Token request fails", "Wrong token URL or grant type", "Use https://<instance>.service-now.com/oauth_token.do and Client Credentials."],
            ["Wrong ServiceNow instance", "Incorrect IP/Domain or Token URL", "Verify the instance hostname in both fields."],
            ["Ticket is not created", "Alert threshold did not breach or ServiceNow action is not selected", "Check alert evaluation and Alert Action."],
            ["Wrong record type", "Incorrect ServiceNow Record Type selected", "Verify Incident/Change/Catalogs/Problems selection."],
            ["Caller error", "Caller is missing/inactive/unauthorized", "Verify the ServiceNow caller/integration user."],
            ["Scope error", "Netraa scope does not match ServiceNow configuration", "Copy the exact scope value from the approved ServiceNow OAuth configuration."],
            ["Connection test fails", "Network/TLS/credentials/configuration issue", "Check domain, port 443, token endpoint, OAuth credentials and ServiceNow availability."],
          ]}
        />
      </section>

      {/* Security Recommendations */}
      <section id="security-recommendations" className="scroll-mt-24 space-y-4">
        <SectionHeading icon={<MdSecurity className="text-xl" />} label="Security Recommendations" id="security-recommendations" />
        <ul className="space-y-3">
          <BulletItem>Use HTTPS only.</BulletItem>
          <BulletItem>Use a dedicated ServiceNow integration user instead of a personal account.</BulletItem>
          <BulletItem>Grant only the permissions required for the selected record types and operations.</BulletItem>
          <BulletItem>Use a dedicated OAuth application for Netraa.</BulletItem>
          <BulletItem>Protect the Client Secret and never commit it to source control.</BulletItem>
          <BulletItem>Do not expose the Client Secret in frontend code.</BulletItem>
          <BulletItem>Rotate credentials according to your organization's security policy.</BulletItem>
          <BulletItem>Use the narrowest practical OAuth scope.</BulletItem>
          <BulletItem>Restrict access to the Netraa Interface Configuration page to authorized administrators.</BulletItem>
        </ul>
      </section>

      {/* Configuration Checklist */}
      <section id="configuration-checklist" className="scroll-mt-24 space-y-4">
        <SectionHeading icon={<MdChecklist className="text-xl" />} label="Configuration Checklist" id="configuration-checklist" />
        <ChecklistGrid items={[
          "ServiceNow instance URL confirmed",
          "OAuth plugin/capability available",
          "OAuth Application Registry created",
          "Client ID recorded securely",
          "Client Secret recorded securely",
          "Client Credentials configured",
          "OAuth Application User configured where required",
          "Required OAuth scope configured",
          "Token URL verified",
          "ServiceNow caller/integration user available",
          "Caller permissions verified",
          "Netraa Configuration Type = Service Now",
          "Netraa Protocol = https",
          "Netraa Domain/IP correct",
          "Netraa Port = 443",
          "Netraa Client selected",
          "Netraa Authentication Type = OAuth 2.0",
          "Netraa Grant Type = Client Credentials",
          "Netraa Client ID entered",
          "Netraa Client Secret entered",
          "Netraa Token URL entered",
          "Netraa Scope entered",
          "Netraa ServiceNow Record Type selected",
          "Netraa Caller configured",
          "Test Connection successful",
          "Interface created",
          "Alert Action = Service Now",
          "Correct ServiceNow interface selected in alert",
          "Threshold configured",
          "Test threshold breach performed",
          "ServiceNow record verified",
        ]} />
      </section>

      {/* Quick Reference */}
      <section id="quick-reference" className="scroll-mt-24 space-y-4">
        <SectionHeading icon={<MdCode className="text-xl" />} label="Quick Reference" id="quick-reference" />
        <div className="space-y-4">
          <div>
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">ServiceNow OAuth Registry:</p>
            <DiagramBlock>{`System OAuth -> Application Registry -> New -> Create an OAuth API endpoint for external clients`}</DiagramBlock>
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Token URL:</p>
            <DiagramBlock>{`https://<instance>.service-now.com/oauth_token.do`}</DiagramBlock>
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">OAuth Grant:</p>
            <DiagramBlock>{`Client Credentials`}</DiagramBlock>
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Netraa:</p>
            <DiagramBlock>{`Admin Configuration -> Interface Configuration
Configuration Type -> Service Now
Authentication -> OAuth 2.0
Grant Type -> Client Credentials
Record Type -> Incident / Change / Catalogs / Problems`}</DiagramBlock>
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Alert:</p>
            <DiagramBlock>{`Alerts -> Set Alert -> Alert Action -> Service Now -> Select Interface`}</DiagramBlock>
          </div>
        </div>
      </section>

      {/* Official ServiceNow References */}
      <section id="official-references" className="scroll-mt-24 space-y-4">
        <SectionHeading icon={<MdLink className="text-xl" />} label="Official ServiceNow References" id="official-references" />
        <div className="grid sm:grid-cols-2 gap-3">
          <ReferenceLink
            label="Create an endpoint for clients to access the instance"
            href="https://www.servicenow.com/docs/r/platform-security/authentication/t_CreateEndpointforExternalClients.html"
          />
          <ReferenceLink
            label="Client Credentials"
            href="https://www.servicenow.com/docs/r/platform-security/authentication/client-credentials.html"
          />
          <ReferenceLink
            label="Client Credentials Grant Workflow"
            href="https://www.servicenow.com/docs/r/platform-security/authentication/client-credentials-grant-workflow.html"
          />
          <ReferenceLink
            label="Add the OAuth Application User"
            href="https://www.servicenow.com/docs/r/platform-security/authentication/add-oauth-application-user.html"
          />
          <ReferenceLink
            label="REST OAuth Example"
            href="https://www.servicenow.com/docs/r/api-reference/rest-api-explorer/r_RESTOAuthExample.html"
          />
        </div>
      </section>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

    </main>
  );
}